import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useTranslation } from '@/localization/useTranslation'
import { Spinner } from '@/components/Loading/Spinner'
import { AuthErrorType, CallbackFormOption, FormOption } from '@/actors/AuthActor/types'
import { useAuth } from '@/hooks/auth/useAuth'
import { useIsClient } from '@uidotdev/usehooks'
import { AuthErrorModal } from '@/actors/AuthActor/components/AuthErrorModal'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { LayoutType } from '@/actors/AuthActor/types'
import { LAYOUT_TYPE, STANDARD_AUTH_FORMS } from '@/constants/auth'
import { Stack } from '@/components/Stack'
import { navigateTo } from '@/utils/url'
import { useTagManager } from '@/hooks/analytics/useTagManager'
import { getErrorMessage } from '@/types/error'

interface CallbackProps {
  handleLayoutTypeChange: (type: LayoutType | null) => void
  handleNavigateToForm?: (form: string) => void
  form?: FormOption
}

const PROVIDER_TOKEN = {
  ['one_time_token']: 'toolbox',
} as const
// Type guard to check if form is a CallbackFormOption
const isCallbackForm = (form: FormOption | undefined): form is CallbackFormOption => {
  return form?.type === 'callback' && 'tokenName' in (form || {})
}

export const Callback: React.FC<CallbackProps> = (props) => {
  const { form, handleLayoutTypeChange, handleNavigateToForm } = props
  const { t } = useTranslation()
  const { sendGtm } = useTagManager()

  // Use type guard to safely access tokenName
  const tokenName = isCallbackForm(form) ? form.tokenName : ''
  const params = useSearchParams()
  const { ssoMutation } = useAuth()
  const { isPending, error } = ssoMutation
  const token = params.get(tokenName)
  const pairCode = params.get('code') // Get pair code from URL
  const hasRequestedAuthRef = useRef(false)
  const isClient = useIsClient()
  const [noTokenError, setNoTokenError] = useState<AuthErrorType | null>(null)

  const handleSsoAuth = useCallback(async (token: string) => {
    const provider = PROVIDER_TOKEN?.[tokenName as keyof typeof PROVIDER_TOKEN] || 'unknown'

    sendGtm.USER_SSO_CALLBACK_RECEIVED({ provider })

    try {
      const res = await ssoMutation.mutateAsync({ [tokenName]: token })

      if (res?.accessToken) {
        sendGtm.USER_SSO_AUTH_COMPLETED({
          provider,
          action: res?.signInAction,
          user: res?.user,
        })

        const url = new URL(window.location.href)
        url.searchParams.delete(tokenName)
        window.history.replaceState({}, '', url.toString())

        // If pair code exists, redirect to pair form after authentication
        if (pairCode && handleNavigateToForm) {
          handleNavigateToForm(STANDARD_AUTH_FORMS.PAIR)
        }
      } else {
        sendGtm.USER_SSO_AUTH_FAILED({
          provider: PROVIDER_TOKEN?.[tokenName as keyof typeof PROVIDER_TOKEN] || 'unknown',
          error: 'No access token received',
        })
      }
    } catch (err) {
      console.error('Error during SSO authentication:', err)
    }
  }, [ssoMutation, tokenName, sendGtm, pairCode, handleNavigateToForm])

  useEffect(() => {
    if (isClient && token && !hasRequestedAuthRef.current && isCallbackForm(form)) {
      let timeoutId: ReturnType<typeof setTimeout> | null = null
      const handleWindowLoad = () => {
        try {
          timeoutId = setTimeout(() => {
            if (!hasRequestedAuthRef.current) {
              hasRequestedAuthRef.current = true
              handleSsoAuth(token)
            }
          }, 3000)
        } catch (error) {
          console.error('Error during SSO authentication:', error)
        }
      }

      if (document.readyState === 'complete') {
        handleWindowLoad()
      } else {
        window.addEventListener('load', handleWindowLoad)
        handleLayoutTypeChange(LAYOUT_TYPE.SINGLE_COLUMN)
      }

      return () => {
        window.removeEventListener('load', handleWindowLoad)
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
      }
    }
  }, [isClient, handleLayoutTypeChange, token, tokenName, form, handleSsoAuth, sendGtm])

  // Handle case when token is missing
  useEffect(() => {
    if (isClient && isCallbackForm(form) && !token && !hasRequestedAuthRef.current) {
      // If no token is present, show error after a short delay
      // to allow any potential token to be added to the URL
      const timeoutId = setTimeout(() => {
        setNoTokenError({
          userMessage: 'Invalid authentication link',
          code: 'NO_TOKEN' as any,
          extra: {}
        })
      }, 1000)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [isClient, form, token])

  useEffect(() => {
    if (error) {
      sendGtm.USER_SSO_AUTH_FAILED({
        provider: PROVIDER_TOKEN?.[tokenName as keyof typeof PROVIDER_TOKEN] || 'unknown',
        error: getErrorMessage(error),
      })
    }
  }, [error, sendGtm, tokenName])

  const handleError = () => {
    window.location.reload()
  }
  const handleRedirect = (path: string) => {
    if (handleNavigateToForm) {
      handleNavigateToForm(path)
      return
    }
    navigateTo(path)
  }

  // Prioritize SSO error over no-token error; hide no-token error when token is present
  const displayError = (error as unknown as AuthErrorType) || (token ? null : noTokenError)

  return (
    <>
      <LoadingOverlay isLoading={isPending} />
      <AuthErrorModal
        error={displayError}
        handleError={handleError}
        handleRedirect={handleRedirect}
      />
      <Stack justify='center' align='center' gap='md'>
        <Spinner />
        <h3>{t('Validating your credentials')}</h3>
      </Stack>
    </>
  )
}